import { useCallback, useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { APP_VERSION } from "@/config/version";
import {
  fetchManifest,
  isSnoozed,
  snooze,
  storeUrlFor,
  updateStatus,
  type UpdateStatus,
  type VersionManifest,
} from "@/utils/update/appUpdate";

/**
 * Tells someone running an old build that a newer one exists.
 *
 * This used to compare a hardcoded "2.1.0" against the bundled manifest.json,
 * which on a native build is a copy of the same file that shipped with the
 * app — so it could never differ, and the check never fired. Worse, the fix
 * it offered was a page reload, which does nothing for an installed app: the
 * WebView reloads the same bundled `dist/`. Now it asks the live site, and
 * on a phone it sends people to the store, where the new build actually is.
 */

const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;

export const VersionCheck = () => {
  const [status, setStatus] = useState<UpdateStatus>("current");
  const [manifest, setManifest] = useState<VersionManifest | null>(null);

  const isNative = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform();

  const check = useCallback(async () => {
    const remote = await fetchManifest(isNative);
    const next = updateStatus(APP_VERSION, remote);
    if (next === "current") return;
    if (next === "optional" && isSnoozed(remote!.latest)) return;
    setManifest(remote);
    setStatus(next);
  }, [isNative]);

  useEffect(() => {
    void check();
    const id = setInterval(() => void check(), CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [check]);

  const storeUrl = storeUrlFor(platform, manifest);
  // A native build with no store link has nothing this button can do: the
  // web fallback below reloads the bundled dist/, which is the same code
  // that is already running. Better to say where the update is than to
  // offer a button that quietly achieves nothing.
  const canAct = Boolean(storeUrl) || !isNative;

  const handleUpdate = () => {
    if (storeUrl) {
      // '_blank' hands off to the system browser under Capacitor, which is
      // what lets an apps.apple.com link jump straight into the App Store app.
      window.open(storeUrl, "_blank");
      return;
    }

    // Web: the newest code is already on the server, so drop the cached copy
    // the service worker is holding and come back fresh.
    if ("caches" in window) {
      void caches.keys().then((names) => names.forEach((n) => void caches.delete(n)));
    }
    if ("serviceWorker" in navigator) {
      void navigator.serviceWorker
        .getRegistrations()
        .then((regs) => regs.forEach((r) => void r.unregister()));
    }
    window.location.reload();
  };

  const handleLater = () => {
    if (manifest) snooze(manifest.latest);
    setStatus("current");
  };

  const required = status === "required";
  if (status === "current" || !manifest) return null;

  return (
    <AlertDialog open onOpenChange={required ? undefined : handleLater}>
      <AlertDialogContent className="soft-card-cream max-w-sm rounded-3xl border-0">
        <AlertDialogHeader>
          <div className="mx-auto mb-2 h-20 w-20">
            <img
              src="/chilli/expression/excited.png"
              alt=""
              aria-hidden
              className="h-full w-full object-contain"
            />
          </div>
          <AlertDialogTitle className="text-center text-xl font-extrabold">
            {required ? "Time to update Chilli" : "A new Chilli is ready!"}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center text-base">
            {manifest.notes ??
              (required
                ? `This version of Chilli Koala is too old to keep working properly. Update to v${manifest.latest} to carry on.`
                : `Version ${manifest.latest} is out, with new things to try. You're on ${APP_VERSION}.`)}
            {!canAct && (
              <span className="mt-2 block font-semibold">
                Search for Chilli Koala in the App Store to get it.
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
          {canAct && (
            <AlertDialogAction
              onClick={handleUpdate}
              className="btn-soft-primary w-full rounded-2xl py-6 text-base font-bold"
            >
              {storeUrl ? "Get the update" : "Refresh now"}
            </AlertDialogAction>
          )}
          {/* A required update with no way to take it would be a locked screen
              on a bricked app, so when there is nothing to act on the dialog
              stays dismissible however urgent it is. */}
          {(!required || !canAct) && (
            <AlertDialogCancel
              onClick={handleLater}
              className={
                canAct
                  ? "mt-0 w-full rounded-2xl border-0 bg-transparent text-sm font-semibold text-muted-foreground"
                  : "btn-soft-primary mt-0 w-full rounded-2xl py-6 text-base font-bold"
              }
            >
              {canAct ? "Maybe later" : "Got it"}
            </AlertDialogCancel>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
